import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { industryService, Industry } from '../services/industryService';
import { absoluteUrl } from '../lib/seo';

const sectorAnchors = [
  'Parcours clients adaptes a votre marche',
  'Automatisation et pilotage alignes sur le terrain',
  'Positionnement premium sans perte de lisibilite'
];

const Activities = () => {
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = industryService.subscribe((updatedIndustries) => {
      setIndustries(updatedIndustries);
      setIsLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-neon-orange" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Nos Activites - Couteau Suisse Digital</title>
        <meta
          name="description"
          content="Découvrez nos solutions premium par secteur d activite. Couteau Suisse Digital adapte design, tech, IA et operations a votre marche."
        />
        <link rel="canonical" href={absoluteUrl('/activites')} />
        <meta property="og:title" content="Nos Activites - Couteau Suisse Digital" />
        <meta
          property="og:description"
          content="Solutions premium par secteur d activite: retail, industrie, services, sante, finance, immobilier et plus."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={absoluteUrl('/activites')} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Nos Activites',
            url: absoluteUrl('/activites'),
            hasPart: industries.map((industry) => ({
              '@type': 'WebPage',
              name: industry.name,
              url: absoluteUrl(`/activites/${industry.id}`),
            })),
          })}
        </script>
      </Helmet>

      <main>
        <section className="py-16 md:py-24 bg-gradient-to-b from-gray-950 via-black to-gray-900 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(255,106,0,0.22),_transparent_38%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_rgba(255,255,255,0.08),_transparent_32%)]" />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-gray-200 backdrop-blur"
                >
                  <span className="h-2 w-2 rounded-full bg-neon-orange" />
                  Solutions par secteur
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.08 }}
                  className="mt-6 max-w-5xl text-4xl font-bold leading-tight text-white md:text-6xl"
                >
                  Une execution digitale
                  <span className="bg-gradient-to-r from-neon-orange to-[#FF8A3D] bg-clip-text text-transparent"> adaptee aux vrais enjeux du secteur</span>
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.16 }}
                  className="mt-6 max-w-3xl text-lg leading-8 text-gray-300 md:text-xl"
                >
                  Nous concevons des systemes plus justes parce qu ils partent d une lecture du marche, des contraintes
                  metier, des objections clients et du niveau de confiance que votre activite doit inspirer.
                </motion.p>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.24 }}
                className="rounded-[30px] border border-white/10 bg-white/5 p-6 text-white backdrop-blur"
              >
                <p className="text-sm uppercase tracking-[0.22em] text-neon-orange">Nos reperes</p>
                <div className="mt-5 space-y-3">
                  {sectorAnchors.map((item) => (
                    <div key={item} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-black/20 p-4">
                      <div className="mt-1 h-2.5 w-2.5 rounded-full bg-neon-orange" />
                      <p className="text-sm leading-6 text-gray-300">{item}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-gradient-to-b from-[#f8f6f2] to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {industries.map((industry, index) => (
                <motion.div
                  key={industry.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.06 }}
                  viewport={{ once: true }}
                  className="group rounded-[30px] border border-gray-200 bg-white p-7 shadow-[0_22px_70px_rgba(17,24,39,0.06)]"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-neon-orange/10 text-3xl">
                        {industry.icon}
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">{industry.name}</h2>
                        <p className="mt-1 text-sm text-gray-500">{industry.stats.projectsCompleted}+ projets</p>
                      </div>
                    </div>
                    <div className="rounded-full border border-gray-200 bg-[#f8f6f2] px-3 py-1 text-xs uppercase tracking-[0.16em] text-gray-500">
                      {industry.stats.successRate}
                    </div>
                  </div>

                  <p className="mt-6 text-base leading-7 text-gray-700">{industry.description}</p>

                  <div className="mt-6 space-y-3">
                    {industry.benefits.slice(0, 3).map((benefit) => (
                      <div key={benefit} className="flex items-start gap-3">
                        <div className="mt-1 h-2.5 w-2.5 rounded-full bg-neon-orange" />
                        <p className="text-sm leading-6 text-gray-600">{benefit}</p>
                      </div>
                    ))}
                  </div>

                  <Link
                    to={`/activites/${industry.id}`}
                    className="mt-7 inline-flex w-full items-center justify-center rounded-full bg-gradient-to-r from-neon-orange to-[#FF4500] px-6 py-3.5 text-base font-semibold text-white"
                  >
                    Explorer ce secteur
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-gradient-to-b from-gray-950 via-black to-gray-900 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,106,0,0.16),_transparent_40%)]" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-3xl font-bold text-white md:text-5xl"
            >
              Votre activite a ses propres regles de jeu.
              <span className="bg-gradient-to-r from-neon-orange to-[#FF8A3D] bg-clip-text text-transparent"> Nous les traduisons en systeme digital.</span>
            </motion.h2>

            <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-gray-300">
              Nous pouvons cadrer une execution plus premium, plus precise et plus raccordee a votre environnement
              commercial et operationnel.
            </p>

            <Link
              to="/contact"
              className="mt-10 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-neon-orange to-[#FF4500] px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-neon-orange/20"
            >
              Parler de votre secteur
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default Activities;
